import "./TableComponent.css";

import React, { useEffect, useRef, useState } from "react";
import { getSafeHtmlFromDb } from "../../../utils/sanitizeSvgFile";
import { statusClass, statusReturn } from "../../../utils/statusData";

import BellIcon from "../../../assets/BellIcon";
import CheckIcon from "../../../assets/CheckIcon.svg";
import DataTable from "react-data-table-component";
import ParkIcon from "../../../assets/ParkIcon.svg";
import { Tooltip } from "react-tooltip";
import { formatDate } from "../../../utils/dateFormat";
import { getActionColumns } from "./getActionColumns";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";
import { useMsal } from "@azure/msal-react";

export const DataTableComponent = ({
    data,
    fullData,
    columns,
    tableName,
    onEditDetails,
    onViewSlotDetails,
    onRevokeUser,
    noDataMessage,
    onViewRCMDetails,
    onViewPICDetails,
    onRevoke,
    pageCount,
    handleView,
    handleAction,
    handlePublishAction,
    handleSurveyExtention,
    onCopyLink,
    onViewDoc,
    handlePublishWithWorkflow,
    handlePublishWithoutWorkflow,
    isAssessmentPublished,
    handleReviseDocument,
    handleDeactivateDocument,
    handleViewSurveyDashboard,
    handleSurveyTemplateDownload,
    handleEditSurvey,
    handlePreviewSurvey,
    handleDocumentHistory,
    onDownloadDoc,
    handleApprove,
    handleReject,
    handleSurveyDataDownload,
    selectedFilter,
    isAscendingOrder,
    handleRePublishAction,
    pagination = true,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(pageCount);
    const [tableColumns, setTableColumns] = useState(columns);
    const { t } = useTranslation();

    // Note:- Following states are used for showing the action options on hover
    const [isClient, setIsClient] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const dotRefs = useRef({});
    const [hoveredRow, setHoveredRow] = useState(null);
    const { authState } = useOktaAuth();
    const { instance } = useMsal();
    const account = instance.getActiveAccount();
    const [expandedRows, setExpandedRows] = useState({});

    // Get user email based on which provider is authenticated
    let userEmail = "";
    let isOktaAuthenticated = authState?.isAuthenticated;
    let isEntraAuthenticated = !!account;

    if (isOktaAuthenticated && authState?.idToken?.claims?.email) {
        userEmail = authState.idToken.claims.email;
    } else if (isEntraAuthenticated && (account?.username || account?.idTokenClaims?.email)) {
        userEmail = account.username || account.idTokenClaims.email;
    }

    useEffect(() => {
        setIsClient(true); //Used to check if the component is rendered on the client side
    }, []);

    // Helper functions for cell rendering
    const renderStatusCell = (row) => {
        let value = "";
        if (
            row.documentWorkFlowStatus &&
            row.documentWorkFlowStatus !== null &&
            row.isActive
        ) {
            value = row.documentWorkFlowStatus;
        } else if (row.documentWorkFlowStatus === "Approved") {
            value = false;
        } else if (row.documentWorkFlowStatus === "Rejected") {
            value = row.documentWorkFlowStatus;
        } else if (row.status !== undefined) {
            
            if(tableName === "Survey"){
                if(row.endDate && new Date().setHours(0, 0, 0, 0) > new Date(row.endDate).setHours(0, 0, 0, 0)){
                    value = "Expired";
                } else {
                    value = row.status;
                }
            } else {
                value = row.status;
            }
        } else if (row.isActive !== undefined) {
            value = row.isActive;
        }
        return (
            <span className={statusClass(statusReturn(value))}>
                {statusReturn(value)}
            </span>
        );
    };

    const renderGuidanceCell = (row, col) => {
        const value = row[col.field];
        return (
            <span
                className="guidanceCellStyle"
                style={{ whiteSpace: "pre-line" }}
            >
                {value ? (
                    <div
                        style={{ whiteSpace: "pre-line" }}
                        dangerouslySetInnerHTML={{ __html: getSafeHtmlFromDb(value) }}
                    />
                ) : (
                    ""
                )}
            </span>
        );
    };

    const renderPublishedCell = (row, col) => (
        <span className="cellStyle">
            {row[col.field] ? (
                <img src={CheckIcon} alt="Check" />
            ) : (
                <img src={ParkIcon} alt="Park" />
            )}
        </span>
    );

    const renderSurveyTitleCell = (row) => {
        const startDate = new Date(row.startDate);
        const endDate = new Date(row.endDate);
        const timeDifference = endDate.getTime() - startDate.getTime();
        const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
        return (
            <span className="bellIconOuterDiv">
                <span>{row.surveyTitle}</span>
                {dayDifference <= 1 && <BellIcon />}
            </span>
        );
    };

    const renderDefaultCell = (row, col) => {
        const value = row[col.field];
        if (value === null || value === "") return "N/A";
        if (
            [
                "modifiedDate",
                "startDate",
                "endDate",
                "addedDate",
                "createdDate",
                "uploadedDate",
                "UploadedDate",
            ].includes(col.field)
        ) {
            return formatDate(value);
        }
        if (col.field === "slotDuration") {
            return (
                <>
                    {formatDate(row.startDate)} - {formatDate(row.endDate)}
                </>
            );
        }
        if (col.field === "comments" || col.field === "remarks") {
            const commentValue = row[col.field];
            return (
                <span
                    data-tooltip-content={commentValue}
                    data-tooltip-id={`tooltip-${row.id}`}
                    className="truncated-text"
                >
                    {commentValue && commentValue.length > 20
                        ? commentValue.slice(0, 30) + "..."
                        : commentValue || "N/A"}
                    <Tooltip
                        id={`tooltip-${row.id}`}
                        place="bottom"
                        className="tableCustomTooltip"
                    />
                </span>
            );
        }
        if (col.field === "image") {
            return (
                <img
                    src={row.image + col.token}
                    alt="template"
                    className="tableImgStyle"
                />
            );
        }
        return value;
    };

    useEffect(() => {
        let updatedColData = columns.map((col) => {
            if (col.field === "status" || col.field === "isActive") {
                return {
                    ...col,
                    cell: (row) => renderStatusCell(row),
                };
            }
            if (col.field === "hitachiGuidance") {
                return {
                    ...col,
                    cell: (row) => renderGuidanceCell(row, col),
                };
            }
            if (col.field === "isPublished") {
                return {
                    ...col,
                    cell: (row) => renderPublishedCell(row, col),
                };
            }
            if (col.field === "surveyTitle" && tableName === "userSurvey") {
                return {
                    ...col,
                    cell: (row) => renderSurveyTitleCell(row),
                };
            }
            // Default cell renderer for all other columns
            return {
                ...col,
                cell: (row) => renderDefaultCell(row, col),
            };
        });

        // Only for survey tables, add expand column
        if (tableName === "Survey") {
            const expandColumn = {
                name: "",
                width: "32px",
                cell: (row) => (
                    <span
                        style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                    </span>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            };
            updatedColData = [expandColumn, ...updatedColData];
        }

        if (tableName !== "viewSlot" && tableName !== "FreeText Responses") {
            const actionColumn = {
                name: t("action"),
                cell: (row, rowIndex) =>
                    getActionColumns({
                        row,
                        rowIndex,
                        onEditDetails,
                        onViewSlotDetails,
                        onRevokeUser,
                        onViewPICDetails,
                        onViewRCMDetails,
                        handleView,
                        onRevoke,
                        tableName,
                        handleAction,
                        handlePublishAction,
                        handleSurveyExtention,
                        onCopyLink,
                        onViewDoc,
                        handlePublishWithoutWorkflow,
                        isAssessmentPublished,
                        handlePublishWithWorkflow,
                        handleSurveyDataDownload,
                        handleSurveyTemplateDownload,
                        handleViewSurveyDashboard,
                        handleEditSurvey,
                        handlePreviewSurvey,
                        handleReviseDocument,
                        handleDeactivateDocument,
                        handleDocumentHistory,
                        handleApprove,
                        onDownloadDoc,
                        handleReject,
                        handleRePublishAction,
                        // Note:- Following states are used for showing the action options on hover
                        isClient,
                        coords,
                        setCoords,
                        hoveredRow,
                        setHoveredRow,
                        dotRefs,
                        userEmail,
                        hasChildRows,
                        fullData
                    }),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            };
            setTableColumns([...updatedColData, actionColumn]);
        } else {
            setTableColumns(updatedColData);
        }
    }, [columns, hoveredRow, coords, expandedRows, tableName]);

    // Helper function to check if a row has children
    const hasChildRows = (row) => {
        if (!fullData || tableName !== "Survey") return false;
        
        const childRows = fullData.filter(
            (item) => item.surveyId === row.surveyId && item !== row
        );
        return childRows.length > 0;
    };

    const capitalizedFirstSort = (rows, selector, direction) => {
        return [...rows].sort((a, b) => {
            const aVal = selector(a) || "";
            const bVal = selector(b) || "";

            // Convert to lowercase for case-insensitive comparison
            const aStr = typeof aVal === "string" ? aVal : String(aVal);
            const bStr = typeof bVal === "string" ? bVal : String(bVal);

            // Compare case-insensitively using localeCompare
            const comparison = aStr?.localeCompare(bStr);

            return direction === "asc" ? comparison : -comparison;
        });
    };

    const renderChildRow = (row) => {
        const parentRow = row.data;
        const columns = tableColumns;
        const childRows = (fullData || []).filter(
            (item) => item.surveyId === parentRow.surveyId && item !== parentRow
        );
        if (childRows.length === 0) {
            return (
                <div style={{ padding: "1rem", color: "#888" }}>
                    No child data available.
                </div>
            );
        }
        return (
            <>
                {childRows.map((child, idx) => (
                    <div key={idx} className="rdt_TableRow child-row-tree" style={{
                        display: "flex",
                        flexDirection: "row",
                        fontSize: "13px",
                        minHeight: "48px",
                        background: "inherit",
                    }}>
                        {columns.map((col, colIdx) => (
                            <div key={colIdx} className="rdt_TableCell"  
                            style={{
                                        flex: colIdx === 0 ? "0 0 80px" : col.width ? `0 0 ${col.width}` : 1,
                                        width: colIdx === 0 ? "80px" : col.width ? col.width : undefined,
                                        minWidth: colIdx === 0 ? "80px" : col.minWidth || undefined,
                                        maxWidth: colIdx === 0 ? "80px" : col.maxWidth || undefined,
                                        padding: "0.5rem",
                                        color: colIdx === 0 ? "transparent" : "#333",
                                        fontWeight: colIdx === 0 ? "normal" : "inherit",
                                        textAlign: colIdx === columns.length - 1 ? "center" : "left",
                                        boxSizing: "border-box",
                                        background: "inherit",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: colIdx === columns.length - 1 ? "center" : "left",
                                        position: col.name === "Action" ? "relative" : undefined, // Ensure relative for action cell

                                    }}>
                                {col.cell
                                    ? col.cell(child, idx)
                                    : col.selector
                                        ? typeof col.selector === "function"
                                            ? col.selector(child, idx)
                                            : child[col.selector]
                                        : ""}
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );
    };
    return (
        <div className="dataTableStyle">
            <DataTable
                keyField={`datatable-${tableName}-${rowsPerPage}-${currentPage}-${data?.length || 0}`}
                columns={tableColumns}
                data={data || []}
                pagination={pagination}
                defaultSortFieldId="endDate"
                defaultSortAsc={
                    isAscendingOrder && isAscendingOrder === true
                        ? true
                        : "false"
                } noDataComponent={
                    tableName === "AdminAssessmentTableView"
                        ? t("noDetailsFound")
                        : tableName === "GroupDetails"
                          ? t("noUsersInDistributionList")
                            : tableName === "slot"
                            ? t("noSlotsFound")
                            : tableName === "template"
                            ? t("noTemplatesFound")
                            : tableName === "AdminAssessmentTable"
                            ? t("noAssessmentsFound")
                            : tableName === "level"
                            ? t("noLevelsFound")
                            : (tableName === "userSurvey" || tableName === "Survey")
                            ? t("noSurveysFound") : t("noDocumentsFound")
                }
                // {data.length > rowsPerPage}
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data?.length || 0}
            paginationRowsPerPageOptions={
                    selectedFilter === "legalEntity"
                        ? [50, 100]
                        : [5, 10, 15, 20, 50, 100]
                }
                paginationComponentOptions={{
                    rowsPerPageText: t("rowsPerPage"),
                }}
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newPerPage, page) => {
                    setRowsPerPage(newPerPage);
                    setCurrentPage(page);
                }}
                // className="custom-data-table"
                className={`custom-data-table ${tableName === "rcm"
                    ? "rcmTableHeight"
                    : tableName === "pic"
                        ? "picTableHeight"
                        : tableName === "OrganizationManagement"
                            ? "orgTableHeight"
                            : "defaultTableHeight"
                    }`}
                responsive={true}
                striped
                fixedHeader
                persistTableHead={true}
                paginationIconFirstPage={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        style={{
                            fill: currentPage === 1 ? "#d3d3d3" : "#c80000",
                            transform: "scaleX(-1)",
                        }}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        role="presentation"
                    >
                        <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path>
                        <path fill="none" d="M0 0h24v24H0V0z"></path>
                    </svg>
                }
                paginationIconLastPage={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        style={{
                            fill:
                                currentPage ===
                                Math.ceil((data?.length || 0) / rowsPerPage)
                                    ? "#d3d3d3"
                                    : "#c80000",
                        }}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        role="presentation"
                    >
                        <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path>
                        <path fill="none" d="M0 0h24v24H0V0z"></path>
                    </svg>
                }
                paginationIconNext={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        style={{
                            fill:
                                currentPage ===
                                Math.ceil((data?.length || 0) / rowsPerPage)
                                    ? "#d3d3d3"
                                    : "#c80000", // Grey if last page
                        }}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        role="presentation"
                    >
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
                        <path d="M0 0h24v24H0z" fill="none"></path>
                    </svg>
                }
                paginationIconPrevious={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        style={{
                            fill: currentPage === 1 ? "#d3d3d3" : "#c80000", // Grey if first page
                        }}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        role="presentation"
                    >
                        <path d="M14 6l1.41 1.41L10.83 12l4.58 4.59L14 18l-6-6z"></path>
                        <path d="M0 0h24v24H0z" fill="none"></path>
                    </svg>
                }
                paginationIconLast={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        style={{
                            fill:
                                currentPage ===
                                Math.ceil((data?.length || 0) / rowsPerPage)
                                    ? "#d3d3d3"
                                    : "#c80000", // Grey if last page
                        }}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        role="presentation"
                    >
                        <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path>
                        <path fill="none" d="M0 0h24v24H0V0z"></path>
                    </svg>
                }
                sortFunction={capitalizedFirstSort}
                {...((tableName === "Survey") && {
                    expandableRows: true,
                    expandableRowsComponent: renderChildRow,
                    expandOnRowClicked: true,
                })}
            />
        </div>
    );
};
